import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardImage } from "@/components/listing-card/CardImage";
import { CardBody } from "@/components/listing-card/CardBody";
import { CardReviews } from "@/components/listing-card/CardReviews";
import { DemoBadge } from "@/components/DemoBadge";
import { trackHubEvent } from "@/utils/hub";
import { trackListingCta } from "@/utils/ctaAnalytics";
import { readJsonPreference, writeJsonPreference } from "@/utils/preferences";
import { SafeWebsiteAction } from "@/components/SafeWebsiteAction";

function contentStatusLabel(status) {
  const labels = {
    "owner-confirmed": "Owner-confirmed details",
    demo: "Demo sample",
    "pending-review": "Details under review",
  };
  return labels[status] || "Evidence-based details";
}

export default function ListingCard({ listing, reviews = [], onReviewClick, onCompare, isComparing }) {
  const navigate = useNavigate();
  const isNew = listing.createdAt && (Date.now() - new Date(listing.createdAt).getTime()) < 14 * 24 * 60 * 60 * 1000;
  const getFavs = () => {
    const value = readJsonPreference("iwmc_favorites", []);
    return Array.isArray(value) ? value : [];
  };
  const [isFav, setIsFav] = useState(() => getFavs().includes(listing.id));
  const toggleFav = (event) => {
    event.stopPropagation();
    const favs = getFavs();
    const next = isFav ? favs.filter((id) => id !== listing.id) : [...favs, listing.id];
    writeJsonPreference("iwmc_favorites", next);
    if (!isFav) trackListingCta(listing.id, "save", "directory_card");
    setIsFav(!isFav);
  };
  const cardReviews = useMemo(() => reviews.filter((review) => {
    if (!review.businessName || !listing.name) return false;
    const business = review.businessName.toLowerCase();
    const name = listing.name.toLowerCase();
    return business.includes(name.slice(0, 8)) || name.includes(business.slice(0, 8));
  }).slice(0, 1), [reviews, listing.name]);

  return (
    <article className="bg-white border-2 border-navy/10 overflow-hidden flex flex-col cursor-pointer group hover:border-navy/45 hover:-translate-y-0.5 transition-[border-color,transform] duration-200" onClick={() => navigate(`/listing/${listing.id}`)} data-testid={`listing-card-${listing.id}`}>
      <CardImage listing={listing} isNew={isNew} isFav={isFav} onToggleFav={toggleFav} onCompare={onCompare} isComparing={isComparing} />
      <CardBody listing={listing} />
      <CardReviews listing={listing} cardReviews={cardReviews} onReviewClick={onReviewClick} />
      <footer className="px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between border-t border-navy/10 bg-slate-50 gap-3">
        <span className="flex items-center gap-2 min-w-0">
          {listing.isDemo && <DemoBadge />}
          <span className="text-gray-500 text-[11px] font-body truncate" data-testid={`listing-content-status-${listing.id}`}>{contentStatusLabel(listing.contentStatus)}</span>
        </span>
        <span className="flex items-center gap-3 flex-shrink-0">
          {!listing.isClaimed && <button onClick={(event) => { event.stopPropagation(); trackHubEvent("claim_prompt_click", listing.id, { placement: "directory_card" }); trackListingCta(listing.id, "claim_open", "directory_card"); navigate(`/listing/${listing.id}?claimPlacement=directory_card#claim`); }} className="text-[10px] text-navy font-mono uppercase font-semibold" data-testid={`claim-card-btn-${listing.id}`}>Claim</button>}
          {listing.website && <SafeWebsiteAction listing={listing} placement="directory_card" className="text-[10px] text-brand-red font-mono uppercase font-bold" testId={`visit-website-btn-${listing.id}`}>Website</SafeWebsiteAction>}
          <button onClick={(event) => { event.stopPropagation(); navigate(`/listing/${listing.id}`); }} className="text-[10px] text-navy font-mono uppercase font-bold" data-testid={`view-venue-btn-${listing.id}`}>View →</button>
        </span>
      </footer>
    </article>
  );
}